import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { navLinks, siteInfo } from "../data/mock";

const SpotifyIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#f4f1ec]/85 backdrop-blur-md border-b border-black/5"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 md:h-20 flex items-center justify-between">
        {/* Left: links */}
        <ul className="hidden md:flex items-center gap-8 text-[15px] tracking-tight">
          {navLinks.map((l) => (
            <li key={l.path}>
              <NavLink
                to={l.path}
                className={({ isActive }) =>
                  `relative pb-1 transition-colors duration-200 hover:text-black ${
                    isActive
                      ? "text-black after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px after:bg-black"
                      : "text-neutral-700"
                  }`
                }
                end={l.path === "/"}
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          className="md:hidden text-black"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>

        {/* Center: logo */}
        <Link
          to="/"
          className="absolute left-1/2 -translate-x-1/2 text-[22px] md:text-[26px] font-medium tracking-[0.02em] text-black"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {siteInfo.shortName}
        </Link>

        {/* Right: spotify */}
        <div className="flex items-center gap-4">
          <a
            href={siteInfo.spotifyUrl}
            target="_blank"
            rel="noreferrer"
            aria-label="Spotify"
            className="text-black hover:text-neutral-600 transition-colors duration-200"
          >
            <SpotifyIcon className="w-6 h-6 md:w-7 md:h-7" />
          </a>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-300 ${
          open ? "max-h-80" : "max-h-0"
        }`}
      >
        <ul className="flex flex-col gap-1 px-6 pb-6 pt-2 bg-[#f4f1ec]/95 backdrop-blur-md">
          {navLinks.map((l) => (
            <li key={l.path}>
              <NavLink
                to={l.path}
                end={l.path === "/"}
                className={({ isActive }) =>
                  `block py-3 text-lg border-b border-black/10 ${
                    isActive ? "text-black font-medium" : "text-neutral-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
};

export default Navbar;
export { SpotifyIcon };
